import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { FileText, Loader2, Receipt, ShieldCheck } from "lucide-react";
import { API } from "@/lib/api";
import { formatDate, money } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";

const HEADS = ["Date", "Fee note", "Matter", "Amount", "Received", "Balance", "Status", ""];

const InvoiceTable = ({ rows, symbol }) => (
  <div className="min-w-0 overflow-x-auto border-x border-b border-slate-200 bg-white">
    <table className="w-full min-w-[720px] text-left">
      <thead>
        <tr className="border-b border-slate-200 bg-slate-50">
          {HEADS.map((h) => (
            <th
              key={h}
              className={`label-caps px-4 py-2.5 ${
                ["Amount", "Received", "Balance"].includes(h) ? "text-right" : ""
              }`}
            >
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {rows.map((row) => (
          <tr key={row.invoice_number} data-testid={`portal-invoice-${row.invoice_number}`}>
            <td className="num px-4 py-3 text-[12px] text-slate-600">{formatDate(row.issue_date)}</td>
            <td className="num px-4 py-3 text-[12px] text-slate-900">{row.invoice_number}</td>
            <td className="max-w-[240px] px-4 py-3">
              <p className="truncate text-[13px] text-slate-800">{row.case_title}</p>
              <p className="truncate text-[11px] text-slate-400">
                {[row.matter_ref, row.forum].filter(Boolean).join(" · ")}
              </p>
            </td>
            <td className="num px-4 py-3 text-right text-[12px] text-slate-800">
              {money(row.total, symbol)}
            </td>
            <td className="num px-4 py-3 text-right text-[12px] text-emerald-700">
              {money(row.received, symbol)}
            </td>
            <td className="num px-4 py-3 text-right text-[13px] font-semibold text-slate-900">
              {money(row.balance, symbol)}
            </td>
            <td className="px-4 py-3">
              <StatusBadge status={row.display_status} />
            </td>
            <td className="px-4 py-3 text-right">
              {row.pdf_url && (
                <a
                  href={row.pdf_url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`portal-invoice-pdf-${row.invoice_number}`}
                  className="text-[12px] font-semibold text-slate-700 underline decoration-slate-300 underline-offset-4 transition-colors duration-150 hover:text-slate-900"
                >
                  PDF
                </a>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function ClientPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/public/portal/${token}`)
      .then((res) => setData(res.data))
      .catch((err) =>
        setError(err?.response?.data?.detail || "This account page is not available."),
      );
  }, [token]);

  if (error) {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-[#FBF6F4] px-6"
        data-testid="portal-error"
      >
        <div className="max-w-sm text-center">
          <img src="/chambers-logo.png" alt="" className="mx-auto h-16 w-16 object-contain" />
          <h1 className="mt-6 font-serif text-2xl text-slate-900">Link unavailable</h1>
          <p className="mt-3 text-sm text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FBF6F4]">
        <Loader2 className="h-5 w-5 animate-spin text-slate-400" data-testid="portal-loading" />
      </div>
    );
  }

  const symbol = data.currency_symbol || "Rs.";
  const { chambers, account, totals, bank, groups } = data;
  const multi = (groups || []).length > 1;
  const payLines = [
    ["Bank", bank.bank_name],
    ["Account name", bank.account_name],
    ["Account no.", bank.account_number],
    ["IFSC", bank.ifsc],
    ["SWIFT", bank.swift],
    ["UPI", bank.upi_id],
  ].filter(([, v]) => v);

  return (
    <div className="min-h-screen bg-[#FBF6F4] pb-20" data-testid="client-portal-page">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-5xl flex-col gap-4 px-5 py-6 sm:flex-row sm:items-center sm:justify-between sm:px-8">
          <div className="flex items-center gap-4">
            <img
              src={data.logo_url || "/chambers-logo.png"}
              alt="Chambers crest"
              className="h-12 w-12 object-contain"
              data-testid="portal-logo"
            />
            <div>
              <p className="font-serif text-lg leading-tight text-slate-900">
                {chambers.chambers_name || chambers.counsel_name}
              </p>
              <p className="text-[11px] uppercase tracking-[0.14em] text-slate-500">
                {[chambers.counsel_name, chambers.designation].filter(Boolean).join(" · ")}
              </p>
            </div>
          </div>
          <div className="text-[11px] leading-relaxed text-slate-500 sm:text-right">
            {chambers.phone && <p className="num">{chambers.phone}</p>}
            {chambers.email && <p>{chambers.email}</p>}
            {chambers.tax_id && (
              <p className="num">
                {chambers.tax_label} {chambers.tax_id}
              </p>
            )}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl space-y-10 px-5 pt-10 sm:px-8">
        <section className="rise">
          <p className="label-caps">
            {data.scope === "firm" ? "Firm account · live" : "Account statement · live"}
          </p>
          <h1 className="mt-3 font-serif text-3xl tracking-tight text-slate-900 lg:text-4xl">
            {account.name}
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            {[account.designation, account.firm_name].filter(Boolean).join(" · ")}
            {account.designation || account.firm_name ? " · " : ""}
            as at {formatDate(data.as_at)}
          </p>
        </section>

        <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
          <div
            className="border border-slate-900 bg-slate-900 p-6 sm:col-span-2"
            data-testid="portal-outstanding"
          >
            <p className="text-[11px] uppercase tracking-[0.14em] text-white/60">
              Currently outstanding
            </p>
            <p className="num mt-3 text-2xl tracking-tight text-white">
              {money(totals.outstanding, symbol)}
            </p>
            {totals.overdue > 0 && (
              <p className="num mt-2 text-[11px] text-amber-300">
                {money(totals.overdue, symbol)} past due
              </p>
            )}
          </div>
          {[
            { label: "Fee notes raised", value: money(totals.billed, symbol), id: "portal-billed" },
            {
              label: "Payments received",
              value: money(totals.received, symbol),
              id: "portal-received",
            },
            {
              label: "Fee notes on account",
              value: String(totals.invoice_count),
              id: "portal-count",
            },
          ].map((k) => (
            <div key={k.label} className="surface min-w-0 p-6" data-testid={k.id}>
              <p className="label-caps">{k.label}</p>
              <p className="num mt-3 text-xl tracking-tight text-slate-900">{k.value}</p>
            </div>
          ))}
        </section>

        <section className="space-y-8">
          <div className="flex flex-wrap items-baseline justify-between gap-3 border-b border-slate-200 pb-3">
            <h2 className="font-serif text-lg text-slate-900">
              {multi ? "Fee notes by instructing lawyer" : "Fee notes"}
            </h2>
            <a
              href={data.statement_url}
              target="_blank"
              rel="noreferrer"
              data-testid="portal-statement-pdf"
              className="inline-flex items-center gap-2 border border-slate-300 bg-white px-4 py-2 text-[12px] font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-50"
            >
              <FileText className="h-3.5 w-3.5" strokeWidth={1.8} />
              Full statement PDF
            </a>
          </div>

          {(groups || []).length === 0 ? (
            <div className="surface p-10 text-center" data-testid="portal-no-invoices">
              <p className="text-sm text-slate-600">Nothing billed on this account yet.</p>
            </div>
          ) : (
            (groups || []).map((group) => (
              <div key={group.contact_id} data-testid={`portal-group-${group.contact_id}`}>
                {multi && (
                  <div className="flex flex-wrap items-baseline justify-between gap-3 pb-3">
                    <div>
                      <h3 className="font-serif text-lg text-slate-900">{group.name}</h3>
                      <p className="text-[11px] uppercase tracking-[0.14em] text-slate-400">
                        {[group.designation, group.firm_name].filter(Boolean).join(" · ")}
                      </p>
                    </div>
                    <p className="num text-[13px] text-slate-900">
                      {money(group.outstanding, symbol)}{" "}
                      <span className="text-[11px] uppercase tracking-[0.12em] text-slate-400">
                        outstanding
                      </span>
                    </p>
                  </div>
                )}
                <InvoiceTable rows={group.rows} symbol={symbol} />
              </div>
            ))
          )}
        </section>

        {data.receipts.length > 0 && (
          <section>
            <div className="flex items-baseline gap-3 border-b border-slate-200 pb-3">
              <h2 className="font-serif text-lg text-slate-900">Receipts</h2>
              <span className="label-caps">{data.receipts.length}</span>
            </div>
            <div className="divide-y divide-slate-100 border-x border-b border-slate-200 bg-white">
              {data.receipts.map((r) => (
                <div
                  key={r.receipt_no || `${r.invoice_number}-${r.paid_on}`}
                  className="flex flex-col gap-2 px-5 py-4 sm:flex-row sm:items-center sm:justify-between"
                  data-testid={`portal-receipt-${r.receipt_no || r.invoice_number}`}
                >
                  <div className="flex items-start gap-3">
                    <Receipt className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" strokeWidth={1.6} />
                    <div>
                      <p className="num text-[13px] text-slate-900">{r.receipt_no || "Receipt"}</p>
                      <p className="text-[11px] text-slate-500">
                        Against {r.invoice_number}
                        {multi && r.contact_name ? ` · ${r.contact_name}` : ""} ·{" "}
                        {formatDate(r.paid_on)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-5 sm:ml-6">
                    <span className="num text-[13px] font-semibold text-emerald-700">
                      {money(r.amount, symbol)}
                    </span>
                    {r.pdf_url && (
                      <a
                        href={r.pdf_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[12px] font-semibold text-slate-700 underline decoration-slate-300 underline-offset-4 transition-colors duration-150 hover:text-slate-900"
                      >
                        PDF
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="grid gap-6 lg:grid-cols-2">
          {payLines.length > 0 && (
            <div className="surface p-6" data-testid="portal-pay-details">
              <p className="label-caps">How to remit</p>
              <dl className="mt-4 space-y-2.5">
                {payLines.map(([label, value]) => (
                  <div key={label} className="flex justify-between gap-4 text-[12px]">
                    <dt className="text-slate-500">{label}</dt>
                    <dd className="num text-right text-slate-900">{value}</dd>
                  </div>
                ))}
              </dl>
              {data.footer_note && (
                <p className="mt-5 border-t border-slate-200 pt-4 text-[11px] leading-relaxed text-slate-500">
                  {data.footer_note}
                </p>
              )}
            </div>
          )}
          <div className="surface min-w-0 overflow-x-auto p-6" data-testid="portal-aging">
            <p className="label-caps">Ageing of what is outstanding</p>
            <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3">
              {data.aging.map((b) => (
                <div key={b.bucket}>
                  <p className="text-[11px] text-slate-500">{b.label}</p>
                  <p className="num mt-1 text-[13px] text-slate-900">{money(b.amount, symbol)}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <footer className="flex flex-col gap-2 border-t border-slate-200 pt-6 text-[11px] text-slate-500 sm:flex-row sm:items-center sm:justify-between">
          <p className="flex items-center gap-2">
            <ShieldCheck className="h-3.5 w-3.5" strokeWidth={1.8} />
            Private link · figures are read live from the chambers ledger
          </p>
          {chambers.address && (
            <p className="whitespace-pre-line sm:text-right">{chambers.address}</p>
          )}
        </footer>
      </main>
    </div>
  );
}
